<script setup lang="ts">
// import { useStorage } from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    categoryIds?: string[];
  }>(),
  {
    categoryIds: () => [],
  }
);

const { t } = useI18n();
const { getItems } = useItemsController();
const { trackEvent } = useGAnalytics();
const route = useRoute();
const GFALExStore = useGFALExStore();
const appStateStore = useAppStateStore();
const merchant = appStateStore.currentMerchant;
// TODO: refactor to use schedules if modalAnnouncent is needed
// const storeOfferEndDate = "2025-07-03T12:00:00+00:00";
// const endDate = new Date(storeOfferEndDate);
// const isModalAnnouncementOpen =
//   new Date() < endDate ? useStorage("showEasterOffer", true) : ref(false);
// const closeAnnouncement = () => {
//   isModalAnnouncementOpen.value = false;
//   useStorage("showEasterOffer", false);
// };

const scrollToCategory = (category: string) => {
  const element = document.getElementById(category);
  if (element) {
    const offset = 120;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

const { data: storeItems, status } = await useAsyncData(
  `get${merchant}Items`,
  async () => {
    if (GFALExStore.GFALExRate === 0) {
      throw new Error("Server down");
    }
    const response = await getItems(merchant);
    const categories = new Set(
      response.records.map((item: Item) => item.categoryId)
    );
    return {
      records: response.records,
      total: response.total,
      categories,
    };
  },
  {
    default: () => ({
      records: [],
      total: 0,
      categories: new Set<string>(),
    }),
    server: false,
    lazy: true,
  }
);

const orderedCategories = computed(() => {
  const categories = [...storeItems.value.categories];

  if (props.categoryIds.length > 0) {
    return props.categoryIds.filter((category) => categories.includes(category));
  }

  return categories;
});
</script>

<template>
  <LoaderCardGridSkeleton
    v-if="
      status == 'pending' || status == 'idle' || !appStateStore.areTidsLoaded
    " />
  <template v-else>
    <CommonNotFoundServer v-if="status === 'error'" />
    <CommonNotFoundItems
      v-else-if="storeItems.total < 1"
      :msg="$t('main.storeMain.noItemsFound')" />
    <template v-else-if="status === 'success' && false">
      <div
        class="mt-6 flex justify-center gap-6 text-neutral-300 font-bold flex-nowrap">
        <template
          v-if="storeItems.categories.size > 1"
          v-for="category in storeItems.categories">
          <p
            ref="allItems"
            @click="scrollToCategory(category)"
            class="w-fit text-center px-2 py-1.5 cursor-pointer whitespace-nowrap z-10 transition-all ease-in-out duration-500 hover:text-primary-500">
            {{ $t(`${merchant}.property.categoryId.values.${category}`, 0) }}
          </p>
        </template>
      </div>
    </template>
    <template
      v-if="status === 'success'"
      v-for="category in orderedCategories"
      :key="category">
      <div
        class="category-gradient h-14 flex items-center justify-center mt-6 mb-2 rounded-sm">
        <h2 :id="category" class="text-2.5xl font-bold text-neutral-200">
          {{ $t(`${merchant}.property.categoryId.values.${category}`, 0) }}
        </h2>
      </div>
      <CommonBanner :category-id="category" />
      <div
        class="grid auto-rows-fr place-content-center grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 1_5xl:grid-cols-4 gap-6 my-6">
        <template
          v-for="(item, index) in storeItems.records.filter(
            (item: Item) => item.categoryId === category
          )"
          :key="item.id">
          <NuxtLinkLocale
            v-if="index < 7"
            :to="`/store/${$route.params.merchant}/${item.id}`"
            :data-test="`link-store-item-${item.id}`"
            @click="
              trackEvent(`click_item_${$route.name as string}`, {
                itemId: item.id,
              })
            ">
            <CardCollectible v-if="item.type === 'COLLECTIBLE'" :item="item" />
            <CardConsumable v-else :item="item" />
          </NuxtLinkLocale>

          <NuxtLinkLocale
            v-else-if="
              storeItems.records.filter(
                (item: Item) => item.categoryId === category
              ).length === 8 && index === 7
            "
            :to="`/store/${$route.params.merchant}/${item.id}`"
            :data-test="`link-store-item-${item.id}`"
            @click="
              trackEvent(`click_item_${$route.name as string}`, {
                itemId: item.id,
              })
            ">
            <CardCollectible v-if="item.type === 'COLLECTIBLE'" :item="item" />
            <CardConsumable v-else :item="item" />
          </NuxtLinkLocale>

          <CardSeeMore v-else-if="index === 8" :categoryId="category" />
        </template>
      </div>
    </template>
  </template>
  <!-- <LazyModalAnnouncement
    v-if="isModalAnnouncementOpen"
    @close="closeAnnouncement" /> -->
</template>

<style scoped>
.category-gradient {
  background: radial-gradient(
    circle,
    rgba(47, 47, 47, 1) 0%,
    rgba(31, 31, 31, 1) 100%
  );
}
</style>
